import { FormItem, Input, Label, Text } from '@ui5/webcomponents-react';
import { SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import {
  IssueType,
  TrainingQueryDetailsFragment,
  useUpdateTrainingQueryMutation
} from 'generated/graphql';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateBaseQueryStore } from 'stores/UpdateBaseQueryStore';
import { useUpdateTrainingQueryStore } from 'stores/UpdateTrainingQueryStore';
import { useEditQueryStore } from 'stores/EditQueryStore';
import { validateString } from 'utils/validator';
import { FormDisplayItem } from '..';
import { useStyles } from '../DisplayQuery.jss';
import dayjs from 'dayjs';

import WrappingType from '@ui5/webcomponents/dist/types/WrappingType.js';

export interface DisplayQueryDetailsTrainingProps {
  queryId: string;
  queryDetails: TrainingQueryDetailsFragment;
}

export const DisplayQueryDetailsTraining = forwardRef<
  SpecificQueryType,
  DisplayQueryDetailsTrainingProps
>((props: DisplayQueryDetailsTrainingProps, ref) => {
  const { queryDetails, queryId } = props;
  const { t } = useTranslation();
  const trainingQueryStore = useUpdateTrainingQueryStore();
  const baseQueryStore = useUpdateBaseQueryStore();
  const { editMode, setCancelEditing } = useEditQueryStore();
  const [, callUpdateQuery] = useUpdateTrainingQueryMutation();
  const classes = useStyles();

  useImperativeHandle(ref, () => ({
    cancel: () => {
      trainingQueryStore.setInitialStateWithProps(queryDetails);
    },
    save: () => {
      return saveQuery();
    },
    isValid: () => {
      return validateAllFields();
    }
  }));

  useEffect(() => {
    trainingQueryStore.setInitialStateWithProps(queryDetails);
  }, []);

  const onChangeXtmProjectName = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLInputElement;
    trainingQueryStore.setXtmProjectName(value);
    validateString(value, trainingQueryStore.setXtmProjectNameValueState);
  };

  const onChangeFileName = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLInputElement;
    trainingQueryStore.setFileName(value);
    validateString(value, trainingQueryStore.setFileNameValueState);
  };

  const saveQuery = async () => {
    const result = await callUpdateQuery({
      input: {
        id: Number(queryId),
        sourceText: baseQueryStore.sourceText,
        targetText: baseQueryStore.targetText,
        deadline: dayjs(baseQueryStore.deadline).format('YYYY-MM-DD'),
        targetLanguage: baseQueryStore.targetLanguage,
        issueType: baseQueryStore.selectedIssueType as unknown as IssueType,
        details: baseQueryStore.details,
        ...(baseQueryStore.assignee && { assignee: baseQueryStore.assignee }),
        distributions: baseQueryStore.distributions,
        resetedDistributions: baseQueryStore.resetedDistributions,
        fileName: trainingQueryStore.fileName,
        xtmProjectName: trainingQueryStore.xtmProjectName,
        qeReview: baseQueryStore.qeReview
      }
    });
    return { error: result.error };
  };

  const validateAllFields = () => {
    const isXtmProjectNameValid = validateXtmProjectName();
    const isFileNameValid = validateFileName();
    return isXtmProjectNameValid && isFileNameValid;
  };

  const validateXtmProjectName = () => {
    return validateString(
      trainingQueryStore.xtmProjectName,
      trainingQueryStore.setXtmProjectNameValueState
    );
  };

  const validateFileName = () => {
    return validateString(trainingQueryStore.fileName, trainingQueryStore.setFileNameValueState);
  };

  const getDetailsData = (): FormDisplayItem[] => {
    return [
      {
        renderComponent: () => (
          <Input
            data-testid="edit-xtm-project-name"
            data-help-id="edit-xtm-project-name"
            aria-labelledby={t('display-query-details-training-xtm-project-name')}
            value={trainingQueryStore.xtmProjectName}
            className={classes.formLineInput}
            valueState={trainingQueryStore.xtmProjectNameValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-xtm-project-name')}
              </Label>
            }
            onChange={onChangeXtmProjectName}
          />
        ),
        dataTestId: 'xtm-project-name',
        data: trainingQueryStore.xtmProjectName,
        i18nTag: t('display-query-details-training-xtm-project-name'),
        required: true
      },
      {
        renderComponent: () => (
          <Input
            value={trainingQueryStore.fileName}
            aria-labelledby={t('display-query-details-training-file-name')}
            data-testid="edit-file-name"
            data-help-id="edit-file-name"
            className={classes.formLineInput}
            valueState={trainingQueryStore.fileNameValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-file-name')}
              </Label>
            }
            onChange={onChangeFileName}
          />
        ),
        dataTestId: 'file-name',
        data: trainingQueryStore.fileName,
        i18nTag: t('display-query-details-training-file-name'),
        required: true
      }
    ];
  };

  let queryDetailsData: FormDisplayItem[] = getDetailsData();

  useEffect(() => {
    queryDetailsData = getDetailsData();
  }, [queryDetails]);

  return (
    <>
      {queryDetailsData.map((entry) => (
        <FormItem
          key={entry.dataTestId}
          labelContent={
            <Label
              id={entry.i18nTag}
              required={editMode && entry.required}
              data-help-id={entry.dataTestId}
              className={classes.formItemLabel}
              wrappingType="None"
            >
              {entry.i18nTag}
            </Label>
          }
        >
          {editMode && entry.editable !== false ? (
            entry.renderComponent()
          ) : (
            <Text
              data-testid={entry.dataTestId}
              aria-labelledby={entry.i18nTag}
              className={classes.formItemText}
            >
              {entry.data}
            </Text>
          )}
        </FormItem>
      ))}
    </>
  );
});
